/**
 * Tasks — the redesigned Task reports page's rules (design "Task Reports & Settings" 1c, 2026-10-07).
 *
 * Plan: C:\Users\nilay\.claude\plans\shiny-gliding-moth.md — "Reports (1c)" and "Data facts".
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE AND CLOCK-FREE (cypress/node-tests/tasks-report-rules.test.js and
 *    tasks-my-day-rules.test.js execute it verbatim). "Today" is passed in as 'YYYY-MM-DD'.
 *
 * ── INPUT ──────────────────────────────────────────────────────────────────────────────────────
 * The page (src/pages/TaskList/ToDo.vue, new branch) reads state.tasks.allTasksForSite (the
 * tasks/getTaskReports listener — every task of the site) and NORMALISES each task first with
 * normalizeTask (below; the caller supplies the office's date format and a moment-timezone
 * "instant → office day" function), so the days here are plain calendar days in the office's terms:
 *   { id, empID, dueYmd, doneYmd, done, deleted, tags }
 *     empID    assignedToEmpID as a String ('' / null → '' = no one) — the old report's attribution
 *     dueYmd   dueAtStr read in the OFFICE's format (DD/MM/YYYY for AFRICA/MASERU, else MM/DD/YYYY)
 *     doneYmd  completedAtStr (always MM/DD/YYYY), else completedAt zoned to the office
 *     done     status === 'COMPLETED'
 *     deleted  deleted === true
 *     tags     the task's `tags` (the old report's tag filter)
 *
 * ── THE RULES (old TaskReports.vue generateReport, with its quirks fixed) ─────────────────────────
 * KEPT from the old report:
 *   • a task counts for its assignee (assignedToEmpID) only;
 *   • on time = completed on or before the due day (days late ≤ 0);
 *   • average days late = Σ max(0, days late) / completed tasks (the old "Avg completion time");
 *   • weeks start on Sunday (moment's default, as the old date picker).
 * FIXED (the old report got these wrong — numbers will differ slightly from the classic page):
 *   1. the range is INCLUSIVE — the old isAfter/isBefore dropped tasks due on the first (and last) day;
 *   2. deleted tasks are skipped — the old report counted them;
 *   3. due days are read in the office's date format (the caller's normalisation) — the old
 *      moment(dueAtStr) read a Maseru 05/10/2026 as May 10;
 *   4. the previous period has the SAME length and ends the day before the range — the old one was
 *      one day short. ("This month" compares with the whole previous calendar month.)
 * NEW (the design's wording): "Still open" = not done AND due before today. An open task not yet due
 * counts in "Due" only (`notDue`). A completed task without any completion date counts as on time
 * (nothing says it was late — the old report dropped it from every bucket and its average went NaN).
 */

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_MS = 86400000;

const pad2 = n => (n < 10 ? '0' : '') + n;
const arr = v => (Array.isArray(v) ? v : []);

/** The range pills, in the design's order. */
export const RANGE_PRESETS = [
  { key: 'thisWeek', label: 'This week' },
  { key: 'lastWeek', label: 'Last week' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'last90', label: 'Last 90 days' },
  { key: 'custom', label: 'Custom' },
];
export const RANGE_KEYS = RANGE_PRESETS.map(r => r.key);

/** The timeliness buckets (bar segments + legend), in the design's order. */
export const SEGMENTS = [
  { key: 'onTime', label: 'On time' },
  { key: 'late12', label: '1–2 days late' },
  { key: 'late36', label: '3–6 days late' },
  { key: 'lateWeek', label: 'A week or more late' },
  { key: 'open', label: 'Still open' },
];

/** An on-time rate below this is drawn red; more open tasks than OPEN_ALERT is drawn red too. */
export const ON_TIME_ALERT_PCT = 70;
export const OPEN_ALERT = 2;

// ---- Days ------------------------------------------------------------------------------------

function partsOf(ymd) {
  const m = typeof ymd === 'string' ? YMD_RE.exec(ymd) : null;
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const back = new Date(Date.UTC(y, mo - 1, d));
  if (back.getUTCMonth() !== mo - 1) return null; // Feb 30 and the like
  return { y, m: mo, d };
}

/** A real calendar day written 'YYYY-MM-DD'. */
export function isYmd(v) {
  return !!partsOf(v);
}

function utcOf(ymd) {
  const p = partsOf(ymd);
  return p ? Date.UTC(p.y, p.m - 1, p.d) : null;
}

function ymdOfUtc(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** 'YYYY-MM-DD' + n days, or null. */
export function addDays(ymd, n) {
  const ms = utcOf(ymd);
  return ms === null ? null : ymdOfUtc(ms + (Number(n) || 0) * DAY_MS);
}

/** Whole days from `a` to `b` (b − a), or null. */
export function daysBetween(a, b) {
  const x = utcOf(a);
  const y = utcOf(b);
  if (x === null || y === null) return null;
  return Math.round((y - x) / DAY_MS);
}

function weekdayIndex(ymd) {
  const ms = utcOf(ymd);
  return ms === null ? null : new Date(ms).getUTCDay(); // 0 = Sunday
}

function firstOfMonth(ymd) {
  const p = partsOf(ymd);
  return p ? `${p.y}-${pad2(p.m)}-01` : null;
}

function lastOfMonth(ymd) {
  const p = partsOf(ymd);
  return p ? ymdOfUtc(Date.UTC(p.y, p.m, 0)) : null;
}

// ---- Normalising one store task ----------------------------------------------------------------

const DMY_OR_MDY_RE = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s|$)/;

/** 'MM/DD/YYYY[ …]' (or 'DD/MM/YYYY[ …]' when `format` says so) → 'YYYY-MM-DD', or null. */
export function ymdFromString(str, format) {
  if (typeof str !== 'string') return null;
  const t = str.trim();
  if (YMD_RE.test(t)) return isYmd(t) ? t : null;
  const m = DMY_OR_MDY_RE.exec(t);
  if (!m) return null;
  const dmy = format === 'DD/MM/YYYY';
  const mo = Number(dmy ? m[2] : m[1]);
  const d = Number(dmy ? m[1] : m[2]);
  const ymd = `${m[3]}-${pad2(mo)}-${pad2(d)}`;
  return isYmd(ymd) ? ymd : null;
}

/** A Date / Firestore Timestamp / { seconds } / epoch-ms value → epoch ms, or null. */
function msOf(v) {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v && typeof v === 'object') {
    if (typeof v.toDate === 'function') {
      try {
        const d = v.toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : null;
      } catch (e) {
        return null;
      }
    }
    if (typeof v.seconds === 'number') return v.seconds * 1000;
  }
  return null;
}

/**
 * One task of state.tasks.allTasksForSite → the item buildReport reads.
 *   opts.dueFormat     the office's dueAtStr format (myDay.js dueAtStrFormat: 'DD/MM/YYYY' for
 *                      AFRICA/MASERU, else 'MM/DD/YYYY')
 *   opts.dayOfInstant  ms → 'YYYY-MM-DD' in the office zone (the caller's moment-timezone)
 * Due day: dueAtStr in the office format; else dueAt (getTaskReports' 'MM/DD/YYYY h:mm:ss a' string,
 * or a Date / Timestamp). Done day: completedAtStr (every writer stamps moment().format('MM/DD/YYYY'));
 * else completedAt (a Timestamp from the listener, a Date, or the store lists' 'MM/DD/YYYY …' string).
 */
export function normalizeTask(task, opts) {
  if (!task || typeof task !== 'object') return null;
  const o = opts || {};
  const zone = typeof o.dayOfInstant === 'function' ? o.dayOfInstant : null;
  const dayOfValue = (v, strFormat) => {
    if (typeof v === 'string') return ymdFromString(v, strFormat);
    const ms = msOf(v);
    if (ms === null || !zone) return null;
    try {
      const d = zone(ms);
      return isYmd(d) ? d : null;
    } catch (e) {
      return null;
    }
  };
  const emp = task.assignedToEmpID;
  return {
    id: task.id,
    empID: emp === undefined || emp === null ? '' : String(emp),
    dueYmd: dayOfValue(task.dueAtStr, o.dueFormat) || dayOfValue(task.dueAt, 'MM/DD/YYYY'),
    doneYmd: dayOfValue(task.completedAtStr, 'MM/DD/YYYY') || dayOfValue(task.completedAt, 'MM/DD/YYYY'),
    done: task.status === 'COMPLETED',
    deleted: task.deleted === true,
    tags: arr(task.tags).filter(g => typeof g === 'string'),
  };
}

// ---- Labels ------------------------------------------------------------------------------------

/** "Oct 4 – 10, 2026", "Sep 27 – Oct 3, 2026", "Dec 28, 2025 – Jan 3, 2026", "Oct 4, 2026". */
export function rangeText(from, to, withYear) {
  const a = partsOf(from);
  const b = partsOf(to);
  if (!a || !b) return '';
  const year = withYear === false ? '' : `, ${b.y}`;
  if (from === to) return `${MONTHS_SHORT[a.m - 1]} ${a.d}${year}`;
  if (a.y !== b.y) return `${MONTHS_SHORT[a.m - 1]} ${a.d}, ${a.y} – ${MONTHS_SHORT[b.m - 1]} ${b.d}, ${b.y}`;
  if (a.m === b.m) return `${MONTHS_SHORT[a.m - 1]} ${a.d} – ${b.d}${year}`;
  return `${MONTHS_SHORT[a.m - 1]} ${a.d} – ${MONTHS_SHORT[b.m - 1]} ${b.d}${year}`;
}

// ---- Ranges ------------------------------------------------------------------------------------

/**
 * The period a pill stands for, on `todayYmd`:
 *   thisWeek   Sunday → Saturday of today's week
 *   lastWeek   the Sunday → Saturday before it
 *   thisMonth  the 1st → the last day of today's month
 *   last90     today − 89 → today
 *   custom     custom.from → custom.to (swapped when reversed; This week when either is missing)
 * → { key, label, from, to, days, text } — `text` finishes the lede "How your team kept up with tasks …".
 * Unknown preset → This week. No valid today → null.
 */
export function rangeFor(preset, todayYmd, custom) {
  if (!isYmd(todayYmd)) return null;
  const key = RANGE_KEYS.indexOf(preset) === -1 ? 'thisWeek' : preset;
  const label = RANGE_PRESETS.find(r => r.key === key).label;
  let from;
  let to;
  if (key === 'custom') {
    const c = custom || {};
    if (!isYmd(c.from) || !isYmd(c.to)) return rangeFor('thisWeek', todayYmd);
    from = c.from <= c.to ? c.from : c.to;
    to = c.from <= c.to ? c.to : c.from;
  } else if (key === 'thisWeek' || key === 'lastWeek') {
    from = addDays(todayYmd, -weekdayIndex(todayYmd) - (key === 'lastWeek' ? 7 : 0));
    to = addDays(from, 6);
  } else if (key === 'thisMonth') {
    from = firstOfMonth(todayYmd);
    to = lastOfMonth(todayYmd);
  } else {
    from = addDays(todayYmd, -89);
    to = todayYmd;
  }
  const p = partsOf(from);
  const text = key === 'thisMonth' ? `due in ${MONTHS_LONG[p.m - 1]} ${p.y}` : `due ${rangeText(from, to)}`;
  return { key, label, from, to, days: daysBetween(from, to) + 1, text };
}

/**
 * The period a range is compared with: the same number of days, ending the day before `from`
 * (the old report's was one day short). "This month" → the whole previous calendar month.
 * → { from, to, days, text } where text is "Compared with …". Null for a null range.
 */
export function previousRange(range) {
  if (!range || !isYmd(range.from) || !isYmd(range.to)) return null;
  if (range.key === 'thisMonth') {
    const to = addDays(range.from, -1);
    const from = firstOfMonth(to);
    const p = partsOf(to);
    const sameYear = p.y === partsOf(range.from).y;
    return {
      from, to, days: daysBetween(from, to) + 1,
      text: `Compared with ${MONTHS_LONG[p.m - 1]}${sameYear ? '' : ` ${p.y}`}`,
    };
  }
  const days = daysBetween(range.from, range.to) + 1;
  const to = addDays(range.from, -1);
  const from = addDays(to, -(days - 1));
  let text;
  if (range.key === 'last90' || range.key === 'custom') {
    text = `Compared with the ${days} ${days === 1 ? 'day' : 'days'} before`;
  } else {
    text = `Compared with ${rangeText(from, to, partsOf(from).y !== partsOf(to).y ? undefined : false)}`;
  }
  return { from, to, days, text };
}

// ---- The report --------------------------------------------------------------------------------

function blankRow(empID) {
  return { empID, due: 0, done: 0, onTime: 0, late12: 0, late36: 0, lateWeek: 0, open: 0, notDue: 0, lateDays: 0, avgLate: null, onTimePct: null };
}

function finish(row) {
  const r = row;
  r.avgLate = r.done ? r.lateDays / r.done : null;
  r.onTimePct = r.done ? Math.round((r.onTime / r.done) * 100) : null;
  return r;
}

/**
 * Per assignee, the tasks DUE in [from, to] (inclusive), deleted ones skipped, narrowed to `tag`
 * when given:
 *   due       every such task
 *   done      completed; split into onTime (≤ 0 days late), late12 (1–2), late36 (3–6), lateWeek (≥ 7)
 *   open      not done and due before today ("Still open")
 *   notDue    not done, due today or later
 *   lateDays  Σ max(0, days late) over the done ones; avgLate = lateDays / done; onTimePct rounded
 * → { rows: [...] (most due first, then empID), totals } — totals has the same fields (empID null).
 */
export function buildReport(items, opts) {
  const o = opts || {};
  const from = o.from;
  const to = o.to;
  const today = isYmd(o.todayYmd) ? o.todayYmd : null;
  const tag = typeof o.tag === 'string' && o.tag !== '' ? o.tag : null;
  const byEmp = {};
  const order = [];
  if (isYmd(from) && isYmd(to)) {
    arr(items).forEach((it) => {
      if (!it || typeof it !== 'object' || it.deleted === true) return;
      const due = it.dueYmd;
      if (!isYmd(due) || due < from || due > to) return;
      if (tag && arr(it.tags).indexOf(tag) === -1) return;
      const key = it.empID === undefined || it.empID === null ? '' : String(it.empID);
      if (!byEmp[key]) {
        byEmp[key] = blankRow(key);
        order.push(key);
      }
      const row = byEmp[key];
      row.due += 1;
      if (it.done) {
        row.done += 1;
        const late = isYmd(it.doneYmd) ? daysBetween(due, it.doneYmd) : 0;
        if (late <= 0) row.onTime += 1;
        else if (late <= 2) row.late12 += 1;
        else if (late <= 6) row.late36 += 1;
        else row.lateWeek += 1;
        row.lateDays += Math.max(0, late);
      } else if (today && due < today) {
        row.open += 1;
      } else {
        row.notDue += 1;
      }
    });
  }
  const rows = order.map(k => finish(byEmp[k]));
  rows.sort((a, b) => (b.due - a.due) || (a.empID < b.empID ? -1 : (a.empID > b.empID ? 1 : 0)));
  const totals = blankRow(null);
  rows.forEach((r) => {
    ['due', 'done', 'onTime', 'late12', 'late36', 'lateWeek', 'open', 'notDue', 'lateDays'].forEach((f) => { totals[f] += r[f]; });
  });
  return { rows, totals: finish(totals) };
}

/** The bar's segments for one row: the buckets with a count, each a share of `due`. */
export function segmentsOf(row) {
  if (!row || !row.due) return [];
  return SEGMENTS.filter(s => row[s.key] > 0).map(s => ({
    key: s.key,
    count: row[s.key],
    pct: (row[s.key] / row.due) * 100,
    tip: `${row[s.key]} ${s.label.charAt(0).toLowerCase()}${s.label.slice(1)}`,
  }));
}

// ---- Stat cards ----------------------------------------------------------------------------------

const MINUS = '\u2212';
const oneDecimal = v => (Math.round(v * 10) / 10).toFixed(1);
const signed = n => (n > 0 ? `+${n}` : (n < 0 ? `${MINUS}${-n}` : '0'));

/**
 * The four cards over `cur` (this period's totals) against `prev` (the previous period's):
 * { key, label, value, delta, trend, sub, tip, sortKey, sortDir }. `trend` is the GOOD direction —
 * 'up' good, 'down' bad, 'flat' neither (the design's StatCard). A delta needs both periods to have
 * a value (no completed tasks → no rate → no delta).
 */
export function statCards(cur, prev) {
  const c = cur || blankRow(null);
  const p = prev || null;

  let otDelta = '';
  let otTrend = 'flat';
  if (c.onTimePct !== null && p && p.onTimePct !== null && p.onTimePct !== undefined) {
    const d = c.onTimePct - p.onTimePct;
    otDelta = d === 0 ? 'No change' : `${d > 0 ? '+' : MINUS}${Math.abs(d)} pts`;
    otTrend = d > 0 ? 'up' : (d < 0 ? 'down' : 'flat');
  }

  let lateDelta = '';
  let lateTrend = 'flat';
  if (c.avgLate !== null && p && p.avgLate !== null && p.avgLate !== undefined) {
    const d = Math.round((c.avgLate - p.avgLate) * 10) / 10;
    lateDelta = d === 0 ? 'No change' : (d < 0 ? `${oneDecimal(-d)} faster` : `${oneDecimal(d)} slower`);
    lateTrend = d < 0 ? 'up' : (d > 0 ? 'down' : 'flat');
  }

  const dueDelta = p ? signed(c.due - p.due) : '';
  const openDelta = p ? signed(c.open - p.open) : '';
  let openTrend = 'flat';
  if (p && c.open > p.open) openTrend = 'down';
  else if (p && c.open < p.open) openTrend = 'up';

  return [
    {
      key: 'onTime', label: 'On time', value: c.onTimePct === null ? '—' : `${c.onTimePct}%`,
      delta: otDelta, trend: otTrend, sub: `${c.onTime} of ${c.done} done on time`,
      tip: 'Sort by lowest on-time rate', sortKey: 'onTimePct', sortDir: 'asc',
    },
    {
      key: 'avgLate', label: 'Average days late', value: c.avgLate === null ? '—' : `${oneDecimal(c.avgLate)} days`,
      delta: lateDelta, trend: lateTrend, sub: 'Completed tasks only',
      tip: 'Sort by most days late', sortKey: 'avgLate', sortDir: 'desc',
    },
    {
      key: 'due', label: 'Tasks due', value: String(c.due), delta: dueDelta, trend: 'flat', sub: `${c.done} done`,
      tip: 'Sort by most tasks', sortKey: 'due', sortDir: 'desc',
    },
    {
      key: 'open', label: 'Still open', value: String(c.open), delta: openDelta, trend: openTrend, sub: 'Past due and not done',
      tip: 'Sort by most open', sortKey: 'open', sortDir: 'desc',
    },
  ];
}

// ---- Sorting -------------------------------------------------------------------------------------

/** The table's sortable columns and the direction a first click sorts in. */
export const SORT_KEYS = { name: 'asc', due: 'desc', done: 'desc', onTimePct: 'desc', avgLate: 'desc', open: 'desc' };

/**
 * A sorted copy. key: name | due | done | onTimePct | avgLate | open; dir 'asc' | 'desc'. Rows
 * without a value (no completed task → no rate, no average) always go last; ties by name, then id.
 */
export function sortRows(rows, key, dir) {
  const k = Object.prototype.hasOwnProperty.call(SORT_KEYS, key) ? key : 'due';
  const sign = dir === 'asc' ? 1 : -1;
  const nameOf = r => String((r && r.name) || '');
  const byName = (a, b) => nameOf(a).localeCompare(nameOf(b)) || (String(a.empID) < String(b.empID) ? -1 : (String(a.empID) > String(b.empID) ? 1 : 0));
  return arr(rows).slice().sort((a, b) => {
    if (k === 'name') return sign * byName(a, b);
    const x = a[k];
    const y = b[k];
    const xn = x === null || x === undefined;
    const yn = y === null || y === undefined;
    if (xn || yn) return (xn ? 1 : 0) - (yn ? 1 : 0) || byName(a, b);
    return sign * (x - y) || byName(a, b);
  });
}

// ---- Tags ----------------------------------------------------------------------------------------

/** The tag select's options: the site's tags plus every tag on an item, deduped, A–Z. */
export function tagOptions(items, siteTags) {
  const out = [];
  const add = (g) => {
    if (typeof g !== 'string') return;
    const t = g.trim();
    if (t && out.indexOf(t) === -1) out.push(t);
  };
  arr(siteTags).forEach(add);
  arr(items).forEach(it => arr(it && it.tags).forEach(add));
  return out.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()) || (a < b ? -1 : (a > b ? 1 : 0)));
}

// ---- CSV -----------------------------------------------------------------------------------------

function cell(v) {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * The Export CSV file: one line per teammate (in the order given) and an "All teammates" line.
 * `names` maps empID → display name. → { filename, text } — text starts with a UTF-8 byte-order
 * mark and uses CRLF so Excel opens names with accents correctly.
 * filename: "Task report, this week.csv" (a preset) / "Task report, 2026-09-01 to 2026-09-30.csv".
 */
export function csv(rows, names, range, totals) {
  const nm = names || {};
  const header = ['Teammate', 'Due', 'Done', 'On time', '1-2 days late', '3-6 days late', 'A week or more late',
    'Still open', 'Not due yet', 'On time %', 'Avg days late'];
  const line = (name, r) => [
    name, r.due, r.done, r.onTime, r.late12, r.late36, r.lateWeek, r.open, r.notDue,
    r.onTimePct === null ? '' : r.onTimePct, r.avgLate === null ? '' : oneDecimal(r.avgLate),
  ].map(cell).join(',');
  const lines = [header.map(cell).join(',')];
  arr(rows).forEach((r) => {
    const name = Object.prototype.hasOwnProperty.call(nm, r.empID) ? nm[r.empID] : (r.name || r.empID || 'No one');
    lines.push(line(name, r));
  });
  if (totals) lines.push(line('All teammates', totals));
  const rg = range || {};
  const what = rg.key === 'custom' || !rg.label ? `${rg.from || ''} to ${rg.to || ''}` : String(rg.label).toLowerCase();
  return { filename: `Task report, ${what}.csv`, text: `\uFEFF${lines.join('\r\n')}\r\n` };
}
